import { useEffect, useRef, useState } from 'react';
import type { ProductCard as ProductCardData } from '@cartiq/shared';

interface Props {
  product: ProductCardData;
  onAddToCart: (productId: string) => void;
  onViewMore: (product: ProductCardData) => void;
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}

export function ProductCard({ product, onAddToCart, onViewMore }: Props) {
  const [added, setAdded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleAdd = () => {
    onAddToCart(product.id);
    setAdded(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAdded(false), 1600);
  };

  return (
    <div className="group w-full overflow-hidden rounded-md border border-border bg-white shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-float animate-pop-in">
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-surface">
        {product.imageUrl && !imageFailed ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center text-mid/40"
            role="img"
            aria-label={`No image available for ${product.name}`}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}
        {!product.inStock && (
          <span className="absolute right-2 top-2 rounded-full bg-error/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Sold out
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5 p-3.5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold leading-snug text-primary">{product.name}</h3>
          <span className="shrink-0 text-sm font-bold text-primary">
            {formatPrice(product.price, product.currency)}
          </span>
        </div>
        {product.description && (
          <p className="line-clamp-2 text-xs leading-relaxed text-mid">{product.description}</p>
        )}

        <div className="mt-2 flex gap-2">
          <button
            disabled={!product.inStock}
            onClick={handleAdd}
            aria-label={added ? `${product.name} added to cart` : `Add ${product.name} to cart`}
            className="flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-sm px-3 py-2 text-xs font-semibold text-white shadow-[0_4px_14px_rgba(108,99,255,0.3)] transition-all hover:brightness-105 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
            style={{ backgroundColor: added ? 'var(--color-success, #16a34a)' : 'var(--brand-accent)' }}
          >
            {added ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Added
              </>
            ) : (
              'Add to Cart'
            )}
          </button>
          <button
            onClick={() => onViewMore(product)}
            aria-label={`View more about ${product.name}`}
            className="min-h-[40px] rounded-sm border border-border px-3 py-2 text-xs font-semibold text-brand transition-colors hover:bg-surface"
          >
            View more
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
